import {List, type Map} from 'immutable';
import type moment from 'moment';
import {type SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';

/**
 * These validators are attached to a SubscriptionModel via `addValidators`, so
 * at runtime `this` is the model instance — not this object literal. The model
 * getters return moment dates / a SubjectModelV2 (despite the validators
 * themselves being keyed by the same getter names and returning validation
 * messages). Describe that runtime `this` context explicitly.
 */
interface SubscriptionValidatorContext {
  getStartDate(): moment.Moment | null | undefined;
  getEndDate(): moment.Moment | null | undefined;
  getSubject(): SubjectModelV2 | null | undefined;
  __changeMap: Map<string, unknown>;
}

export const subscriptionModelValidators = {
  getStartDate(this: SubscriptionValidatorContext): List<string> | null | undefined {
    if (!this.getStartDate()) {
      return List(['A start date is required']);
    }

    // validate start is before end
    if (this.getEndDate() && !this.getEndDate()!.isAfter(this.getStartDate())) {
      return List(['Start date must be before end date']);
    }

    return null;
  },

  getEndDate(this: SubscriptionValidatorContext): List<string> | null | undefined {
    if (!this.getEndDate()) {
      return List(['An end date is required']);
    }

    // validate start is before end
    if (this.getStartDate() && !this.getEndDate()!.isAfter(this.getStartDate())) {
      return List(['End date must come after start date']);
    }

    return null;
  },

  getSubject(this: SubscriptionValidatorContext): List<string> | null | undefined {
    // we look at the changemap first as this is a relationship. This is hacky.
    const subjectBeingAdded = this.__changeMap.getIn(['__relationships', 'subject_v2']);
    const existingSubject = this.getSubject();
    if (!subjectBeingAdded && existingSubject && existingSubject.isEmpty()) {
      return List(['Subject is required']);
    }

    return null;
  }
};
